import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import styles from './BatchAllNotesTool.module.css';

const NOTES_MODEL = 'gpt-5-nano';

function ts() {
  return new Date().toLocaleTimeString('zh-CN', { hour12: false });
}

function BatchAllNotesTool() {
  const [allVolumes, setAllVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [orderedVolumeIds, setOrderedVolumeIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [running, setRunning] = useState(false);
  const [currentVolIdx, setCurrentVolIdx] = useState(-1);
  const [currentVolId, setCurrentVolId] = useState(null);
  const [notesStep, setNotesStep] = useState({ status: 'idle', progress: 0, detail: '' });
  const [volumeResults, setVolumeResults] = useState(new Map());
  const [logs, setLogs] = useState([]);
  const [globalError, setGlobalError] = useState('');
  const cancelledRef = useRef(false);
  const logEndRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const volumeMap = useMemo(() => {
    const m = new Map();
    for (const v of allVolumes) m.set(v.id, v);
    return m;
  }, [allVolumes]);

  const orderedVolumes = useMemo(() => {
    return orderedVolumeIds.map((id) => volumeMap.get(id)).filter(Boolean);
  }, [orderedVolumeIds, volumeMap]);

  const selectedVolumes = useMemo(() => {
    return orderedVolumes.filter((v) => selectedIds.has(v.id));
  }, [orderedVolumes, selectedIds]);

  const totalChapters = useMemo(() => {
    return selectedVolumes.reduce((sum, v) => sum + (v.chapters?.length || 0), 0);
  }, [selectedVolumes]);

  const addLog = useCallback((text, type = 'info') => {
    setLogs((prev) => [...prev, { time: ts(), text, type }]);
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const loadVolumes = useCallback(async () => {
    setVolumesLoading(true);
    try {
      const { data } = await api.get('/volumes/', {
        params: { limit: 500, include_chapters: true },
      });
      const list = Array.isArray(data) ? data : [];
      setAllVolumes(list);
      setOrderedVolumeIds(list.map((v) => v.id));
      setSelectedIds(new Set(list.map((v) => v.id)));
    } catch {
      addLog('获取卷列表失败', 'error');
    } finally {
      setVolumesLoading(false);
    }
  }, [addLog]);

  useEffect(() => {
    loadVolumes();
  }, [loadVolumes]);

  const moveVolume = useCallback((fromIdx, toIdx) => {
    setOrderedVolumeIds((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, removed);
      return next;
    });
  }, []);

  const handleDragStart = useCallback((idx) => {
    dragItem.current = idx;
  }, []);

  const handleDragEnter = useCallback((idx) => {
    dragOverItem.current = idx;
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
      moveVolume(dragItem.current, dragOverItem.current);
    }
    dragItem.current = null;
    dragOverItem.current = null;
  }, [moveVolume]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === orderedVolumeIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orderedVolumeIds));
    }
  }, [selectedIds.size, orderedVolumeIds]);

  const runVolumeNotes = useCallback(
    async (volume) => {
      const chapters = Array.isArray(volume.chapters)
        ? [...volume.chapters].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        : [];

      if (chapters.length === 0) {
        addLog(`[卷 ${volume.id}] 没有章节，跳过`, 'error');
        return 'skipped';
      }

      const volLabel = volume.novel?.title
        ? `${volume.novel.title} — ${volume.title}`
        : volume.title || `卷 ${volume.id}`;

      addLog(`────── 开始处理卷: ${volLabel}（${chapters.length} 章）──────`);

      let freshChapters = chapters;
      try {
        const { data } = await api.get('/volumes/', {
          params: { limit: 500, include_chapters: true },
        });
        const list = Array.isArray(data) ? data : [];
        const freshVol = list.find((v) => v.id === volume.id);
        if (freshVol?.chapters) {
          freshChapters = [...freshVol.chapters].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
        }
      } catch {
        addLog('[预检查] 获取最新数据失败，使用缓存', 'error');
      }

      // 仅对「有 content_trans 且 无 ai_notes」的章节生成笔记
      const needNotesChapters = freshChapters.filter(
        (c) => c.has_content_trans && !c.has_ai_notes,
      );
      const needNotesIds = needNotesChapters.map((c) => c.id);

      if (needNotesIds.length === 0) {
        addLog(`[笔记] 本卷无待处理章节（已有笔记或缺少翻译），跳过`, 'success');
        return 'skipped';
      }

      setNotesStep({ status: 'running', progress: 0, detail: `正在为 ${needNotesIds.length} 章生成 AI 笔记…` });
      addLog(`[笔记] 开始为 ${needNotesIds.length} 个章节生成 AI 笔记`);

      try {
        const { data } = await api.post(
          '/ai-notes/batch',
          { chapter_ids: needNotesIds, model: NOTES_MODEL },
          { timeout: 0 },
        );
        const succeeded = data.succeeded ?? 0;
        const total = data.total_requested ?? needNotesIds.length;
        addLog(`[笔记] 完成，成功 ${succeeded}/${total} 章`, succeeded === total ? 'success' : 'error');
        setNotesStep({ status: succeeded > 0 ? 'done' : 'failed', progress: 100, detail: `${succeeded}/${total} 章完成` });
        return succeeded > 0 ? 'done' : 'failed';
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || '未知错误';
        addLog(`[笔记] 失败: ${msg}`, 'error');
        setNotesStep({ status: 'failed', progress: 0, detail: msg });
        return 'failed';
      }
    },
    [addLog],
  );

  const startAllPipeline = useCallback(async () => {
    if (selectedVolumes.length === 0) {
      setGlobalError('请至少选择一个卷');
      return;
    }
    setGlobalError('');
    setLogs([]);
    setVolumeResults(new Map());
    setRunning(true);
    cancelledRef.current = false;

    addLog(`===== 全量笔记启动 =====`);
    addLog(`共 ${selectedVolumes.length} 个卷，${totalChapters} 个章节；仅执行 AI 笔记步骤，已有笔记或无翻译的章节将跳过。`);

    for (let i = 0; i < selectedVolumes.length; i++) {
      if (cancelledRef.current) {
        addLog('用户取消，跳过后续卷', 'error');
        setVolumeResults((prev) => {
          const next = new Map(prev);
          for (let j = i; j < selectedVolumes.length; j++) {
            next.set(selectedVolumes[j].id, 'skipped');
          }
          return next;
        });
        break;
      }

      const vol = selectedVolumes[i];
      setCurrentVolIdx(i);
      setCurrentVolId(vol.id);
      setNotesStep({ status: 'idle', progress: 0, detail: '' });

      addLog(`\n========== [${i + 1}/${selectedVolumes.length}] 卷: ${vol.novel?.title || ''} — ${vol.title || `#${vol.id}`} ==========`);

      let result;
      try {
        result = await runVolumeNotes(vol);
      } catch (err) {
        result = 'failed';
        addLog(`卷 ${vol.id} 异常: ${err?.message || '未知'}`, 'error');
      }

      setVolumeResults((prev) => {
        const next = new Map(prev);
        next.set(vol.id, result);
        return next;
      });
    }

    setCurrentVolIdx(-1);
    setCurrentVolId(null);
    setNotesStep({ status: 'idle', progress: 0, detail: '' });
    setRunning(false);
    addLog(`\n===== 全量笔记结束 =====`, 'success');
  }, [selectedVolumes, totalChapters, addLog, runVolumeNotes]);

  const handleStop = useCallback(() => {
    cancelledRef.current = true;
    addLog('用户请求停止…', 'error');
  }, [addLog]);

  const volStatusBadge = (volId) => {
    const r = volumeResults.get(volId);
    if (!r) {
      if (currentVolId === volId) return <span className={styles.volBadgeRunning}>处理中…</span>;
      return null;
    }
    switch (r) {
      case 'done': return <span className={styles.volBadgeDone}>✓ 完成</span>;
      case 'failed': return <span className={styles.volBadgeFailed}>✕ 失败</span>;
      case 'skipped': return <span className={styles.volBadgeSkipped}>— 跳过</span>;
      default: return null;
    }
  };

  const doneCount = [...volumeResults.values()].filter((v) => v === 'done').length;
  const failedCount = [...volumeResults.values()].filter((v) => v === 'failed').length;

  return (
    <div className={styles.page}>
      <div className={styles.configCard}>
        <h2 className={styles.configTitle}>全量笔记</h2>
        <p className={styles.configHint}>
          列出所有卷，按顺序逐卷执行「AI 笔记」步骤。已有 AI 笔记的章节跳过；无 content_trans（未翻译）的章节跳过。
        </p>

        {globalError && (
          <div className={styles.errorMessage}>{globalError}</div>
        )}

        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.startBtn}
            onClick={startAllPipeline}
            disabled={running || volumesLoading || selectedVolumes.length === 0}
          >
            开始全量笔记
          </button>
          {running && (
            <button type="button" className={styles.stopBtn} onClick={handleStop}>
              停止
            </button>
          )}
          <button
            type="button"
            className={styles.selectAllBtn}
            onClick={toggleSelectAll}
            disabled={running || orderedVolumeIds.length === 0}
          >
            {selectedIds.size === orderedVolumeIds.length ? '取消全选' : '全选'}
          </button>
          <button
            type="button"
            className={styles.selectAllBtn}
            onClick={loadVolumes}
            disabled={running}
          >
            刷新列表
          </button>
        </div>
      </div>

      <div className={styles.volumeListCard}>
        <div className={styles.volumeListHeader}>
          <h3 className={styles.volumeListTitle}>
            卷列表
            <span className={styles.volumeListCount}>
              已选 {selectedVolumes.length} / {orderedVolumes.length} 卷，共 {totalChapters} 章
            </span>
          </h3>
        </div>

        {running && (
          <div className={styles.overallProgress}>
            <div className={styles.overallProgressBar}>
              <div
                className={styles.overallProgressFill}
                style={{
                  width: `${selectedVolumes.length > 0 ? Math.round(((currentVolIdx + 1) / selectedVolumes.length) * 100) : 0}%`,
                }}
              />
            </div>
            <span className={styles.overallProgressText}>
              {currentVolIdx + 1}/{selectedVolumes.length} 卷 | 完成: {doneCount} | 失败: {failedCount}
            </span>
          </div>
        )}

        <div className={styles.volumeList}>
          {orderedVolumes.map((vol, idx) => {
            const chapCount = vol.chapters?.length || 0;
            const isSelected = selectedIds.has(vol.id);
            const isCurrent = currentVolId === vol.id;
            const novelTitle = vol.novel?.title || '';

            return (
              <div
                key={vol.id}
                className={`${styles.volumeItem} ${isCurrent ? styles.volumeItemCurrent : ''} ${!isSelected ? styles.volumeItemDisabled : ''}`}
                draggable={!running}
                onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
              >
                <div className={styles.dragHandle} title="拖拽排序">
                  <span className={styles.dragIcon}>⠿</span>
                </div>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(vol.id)}
                    disabled={running}
                    className={styles.checkbox}
                  />
                </label>
                <span className={styles.volumeIdx}>{idx + 1}</span>
                <div className={styles.volumeInfo}>
                  <span className={styles.volumeTitle}>
                    {novelTitle ? `${novelTitle} — ` : ''}{vol.title || `卷 ${vol.id}`}
                  </span>
                  <span className={styles.volumeMeta}>{chapCount} 章</span>
                </div>
                <div className={styles.volumeStatus}>{volStatusBadge(vol.id)}</div>
                <div className={styles.moveButtons}>
                  <button
                    type="button"
                    className={styles.moveBtn}
                    onClick={() => idx > 0 && moveVolume(idx, idx - 1)}
                    disabled={running || idx === 0}
                    title="上移"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={styles.moveBtn}
                    onClick={() => idx < orderedVolumeIds.length - 1 && moveVolume(idx, idx + 1)}
                    disabled={running || idx === orderedVolumes.length - 1}
                    title="下移"
                  >
                    ↓
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(running || currentVolIdx >= 0) && (
        <div className={styles.stepsCard}>
          <h3 className={styles.stepsTitle}>
            {currentVolId
              ? `当前卷 — ${volumeMap.get(currentVolId)?.title || `#${currentVolId}`}`
              : '进度'}
          </h3>
          <div className={styles.stepItem}>
            <div className={styles.stepIndicatorCol}>
              <div
                className={`${styles.stepDot} ${
                  notesStep.status === 'running' ? styles.stepDotRunning
                    : notesStep.status === 'done' ? styles.stepDotDone
                      : notesStep.status === 'failed' ? styles.stepDotFailed
                        : styles.stepDotIdle
                }`}
              >
                {notesStep.status === 'done' ? '✓' : notesStep.status === 'failed' ? '✕' : '1'}
              </div>
            </div>
            <div className={styles.stepContent}>
              <div className={styles.stepHeader}>
                <span className={styles.stepName}>AI 笔记</span>
                <span
                  className={`${styles.stepStatusText} ${
                    notesStep.status === 'running' ? styles.stepStatusRunning
                      : notesStep.status === 'done' ? styles.stepStatusDone
                        : notesStep.status === 'failed' ? styles.stepStatusFailed : ''
                  }`}
                >
                  {notesStep.status === 'idle' ? '等待' : notesStep.status === 'running' ? '进行中' : notesStep.status === 'done' ? '完成' : notesStep.status === 'failed' ? '失败' : ''}
                </span>
              </div>
              {notesStep.detail && <div className={styles.stepDetail}>{notesStep.detail}</div>}
              {(notesStep.status === 'running' || notesStep.status === 'done') && (
                <div className={styles.progressBarWrapper}>
                  <div
                    className={`${styles.progressBarFill} ${notesStep.status === 'done' ? styles.progressBarFillDone : ''}`}
                    style={{ width: `${notesStep.progress}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.logCard}>
        <div className={styles.logHeader}>
          <h3 className={styles.logTitle}>日志</h3>
          {logs.length > 0 && (
            <button type="button" className={styles.logClearBtn} onClick={() => setLogs([])}>
              清空
            </button>
          )}
        </div>
        <div className={styles.logBody}>
          {logs.length === 0 && <div style={{ color: 'var(--gray-40)' }}>暂无日志</div>}
          {logs.map((entry, i) => (
            <div key={i} className={styles.logEntry}>
              <span className={styles.logTime}>{entry.time}</span>
              <span
                className={`${styles.logText} ${
                  entry.type === 'error' ? styles.logError : entry.type === 'success' ? styles.logSuccess : ''
                }`}
              >
                {entry.text}
              </span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

export default BatchAllNotesTool;
